import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Markdown from 'react-markdown';
import { MdCalendarToday, MdArrowDropDown, MdEdit, MdDelete, MdAdd, MdSave, MdClose } from 'react-icons/md';
import PieChart from '../components/PieChart';
import { Helmet } from "react-helmet";


const API = process.env.REACT_APP_API_URL || '';

export default function WeeklyReportDetailPage({ user: userProp }) {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [reportsList, setReportsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    week: ''
  });
  const [templateData, setTemplateData] = useState({
    movies: [{
      name: '',
      weekCollections: '',
      totalCollections: '',
      drop: '',
      distribution: {
        overseas: '',
        kannada: '',
        telugu: '',
        hindi: '',
        tamil: '',
        malayalam: '',
        other: ''
      }
    }]
  });
  const [useTemplate, setUseTemplate] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [allMovies, setAllMovies] = useState([]);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const weekParam = searchParams.get('week');
    
    if (slug) {
      // Slug-based URL always takes priority
      fetchReportBySlug(slug);
    } else if (weekParam) {
      // Fallback to week parameter for legacy links
      fetchReportByWeek(weekParam);
    } else {
      // No slug or week -> show latest
      fetchLatestReport();
    }
    // Don't fetch reports list until user opens dropdown
    // Don't fetch all movies until user opens create/edit form
    checkAdminStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, searchParams]);

  const fetchAllMovies = async () => {
    try {
      // Fetch movies from both upcoming and old tabs to search by name
      const [upcomingRes, oldRes] = await Promise.all([
        axios.get(`${API}/api/movies?tab=upcoming&page=1&limit=100`),
        axios.get(`${API}/api/movies?tab=old&page=1&limit=100`)
      ]);
      const allMoviesList = [
        ...(upcomingRes.data.movies || []),
        ...(oldRes.data.movies || [])
      ];
      setAllMovies(allMoviesList);
    } catch (err) {
      // Error fetching movies
    }
  };

  const findMovieByName = (movieName) => {
    if (!movieName || !allMovies.length) return null;
    // Try exact match first
    let movie = allMovies.find(m => 
      m.title.toLowerCase().trim() === movieName.toLowerCase().trim()
    );
    // If no exact match, try partial match
    if (!movie) {
      movie = allMovies.find(m => 
        m.title.toLowerCase().includes(movieName.toLowerCase()) ||
        movieName.toLowerCase().includes(m.title.toLowerCase())
      );
    }
    return movie;
  };

  // Parse markdown content to extract movie data
  const parseMoviesFromContent = (content) => {
    if (!content) return [];
    
    const movies = [];
    const lines = content.split('\n');
    let currentMovie = null;
    let inDistribution = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check for movie heading (### 1. Movie Name)
      const movieMatch = line.match(/^###\s+\d+\.\s+(.+)$/);
      if (movieMatch) {
        if (currentMovie) {
          movies.push(currentMovie);
        }
        currentMovie = {
          name: movieMatch[1],
          weekCollections: '',
          totalCollections: '',
          distribution: {
            overseas: '',
            kannada: '',
            telugu: '',
            hindi: '',
            tamil: '',
            malayalam: '',
            other: ''
          }
        };
        inDistribution = false;
        continue;
      }

      if (currentMovie) {
        // Check for week collections
        const weekMatch = line.match(/\*\*This Week's Collections:\*\*\s+(.+)/);
        if (weekMatch) {
          currentMovie.weekCollections = weekMatch[1].trim();
          continue;
        }

        // Check for total collections
        const totalMatch = line.match(/\*\*Total Collections \(Till Now\):\*\*\s+(.+)/);
        if (totalMatch) {
          currentMovie.totalCollections = totalMatch[1].trim();
          continue;
        }

        // Check for drop
        const dropMatch = line.match(/\*\*Drop:\*\*\s+(.+)/);
        if (dropMatch) {
          currentMovie.drop = dropMatch[1].trim();
          continue;
        }

        // Check for distribution section
        if (line.includes('Distribution Breakdown')) {
          inDistribution = true;
          continue;
        }

        // Parse distribution items
        if (inDistribution && line.trim().startsWith('-')) {
          const distMatch = line.match(/-\s+(\w+):\s+(.+)/);
          if (distMatch) {
            const region = distMatch[1].toLowerCase();
            const amount = distMatch[2].trim();
            if (currentMovie.distribution.hasOwnProperty(region)) {
              currentMovie.distribution[region] = amount;
            }
          }
          continue;
        }
      }
    }

    if (currentMovie) {
      movies.push(currentMovie);
    }

    return movies;
  };

  // Render movies in structured format with text layout
  const renderMovies = (content) => {
    const movies = parseMoviesFromContent(content);
    
    if (movies.length === 0) {
      // Fallback to markdown rendering if no structured data
      return <Markdown>{content}</Markdown>;
    }

    // Parse the original markdown to maintain structure but enhance with clickable titles and pie charts
    const lines = content.split('\n');
    const elements = [];
    let currentMovieIndex = -1;
    let inDistribution = false;
    let currentDistribution = null;

    lines.forEach((line, index) => {
      const trimmed = line.trim();
      
      // Check for movie heading (### 1. Movie Name)
      const movieMatch = trimmed.match(/^###\s+\d+\.\s+(.+)$/);
      if (movieMatch) {
        currentMovieIndex = movies.findIndex(m => m.name === movieMatch[1]);
        inDistribution = false;
        currentDistribution = null;
        
        if (currentMovieIndex >= 0) {
          const movie = movies[currentMovieIndex];
          const movieData = findMovieByName(movie.name);
          
          elements.push(
            <h3 
              key={`h3-${index}`}
              className={movieData ? 'clickable-movie-title' : ''}
              onClick={() => {
                if (movieData) {
                  navigate(`/movie/${movieData.slug || movieData.id}`);
                }
              }}
              style={{ cursor: movieData ? 'pointer' : 'default' }}
            >
              {trimmed.replace('### ', '')}
              {movieData && <span style={{ marginLeft: '8px', fontSize: '0.8em', color: 'var(--accent)' }}>→</span>}
            </h3>
          );
        } else {
          elements.push(<h3 key={`h3-${index}`}>{trimmed.replace('### ', '')}</h3>);
        }
        return;
      }

      // Check for week collections - modify to show "This Week | Total Gross"
      if (currentMovieIndex >= 0 && trimmed.includes("This Week's Collections")) {
        const movie = movies[currentMovieIndex];
        const weekMatch = trimmed.match(/\*\*This Week's Collections:\*\*\s+(.+)/);
        const weekValue = weekMatch ? weekMatch[1].trim() : movie.weekCollections;
        const totalValue = movie.totalCollections;
        
        elements.push(
          <p key={`collections-${index}`} className="movie-collections-text">
            <strong>This Week's Collections:</strong> {weekValue} <span style={{ margin: '0 8px', color: 'var(--text-tertiary)' }}>|</span> <strong>Total Collections (Till Now):</strong> {totalValue}
          </p>
        );
        return;
      }

      // Skip the original total collections line since we combined it above
      if (currentMovieIndex >= 0 && trimmed.includes("Total Collections (Till Now)")) {
        return;
      }

      // Check for distribution section - replace with pie chart
      if (trimmed.includes('Distribution Breakdown')) {
        inDistribution = true;
        if (currentMovieIndex >= 0) {
          currentDistribution = movies[currentMovieIndex].distribution;
        }
        // Render the header
        elements.push(
          <p key={`dist-header-${index}`}>
            <strong>Distribution Breakdown:</strong>
          </p>
        );
        // Show pie chart immediately if we have distribution data
        if (currentDistribution) {
          // Check if we have any valid distribution data
          const hasData = Object.values(currentDistribution).some(v => {
            if (!v) return false;
            const str = v.toString().trim();
            if (!str || str === '') return false;
            const num = parseFloat(str.replace(/[^0-9.]/g, '') || '0');
            return !isNaN(num) && num > 0;
          });
          
          if (hasData) {
            const distEntries = Object.entries(currentDistribution)
              .filter(([_, value]) => {
                if (!value) return false;
                const str = value.toString().trim();
                if (!str || str === '') return false;
                const num = parseFloat(str.replace(/[^0-9.]/g, '') || '0');
                return !isNaN(num) && num > 0;
              })
              .map(([key, value]) => ({
                key: key.charAt(0).toUpperCase() + key.slice(1),
                value: value.toString().trim()
              }));

            if (distEntries.length > 0) {
              elements.push(
                <div key={`pie-${index}`} className="distribution-with-chart">
                  <PieChart data={currentDistribution} size={100} />
                  <div className="distribution-list">
                    {distEntries.map((entry, idx) => (
                      <div key={idx} className="distribution-item">
                        <span className="distribution-label">{entry.key}:</span>
                        <span className="distribution-value">{entry.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
          }
        }
        return;
      }

      // Skip distribution list items (we already showed them with pie chart)
      if (inDistribution && trimmed.startsWith('-')) {
        return; // Skip the markdown list items
      }

      // Skip separator lines (---) - don't render them
      if (trimmed === '---') {
        inDistribution = false;
        currentDistribution = null;
        return; // Don't render the separator
      }
      
      // Reset distribution mode on empty line if we're done
      if (inDistribution && trimmed === '') {
        inDistribution = false;
        currentDistribution = null;
      }

      // Regular markdown rendering for other lines
      if (trimmed.length > 0) {
        elements.push(
          <Markdown key={`md-${index}`} components={{
            p: ({children}) => <p>{children}</p>,
            strong: ({children}) => <strong>{children}</strong>,
            li: ({children}) => <li>{children}</li>
          }}>
            {trimmed}
          </Markdown>
        );
      }
    });

    // Add table at the end if we have movies
    if (movies.length > 0) {
      elements.push(
        <div key="movies-table" className="top-movies-table-container">
          <h2 className="top-movies-table-title">Top Movies This Week</h2>
          <table className="top-movies-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Movie Name</th>
                <th>This Week Gross</th>
                <th>Total Gross</th>
                <th>Drop</th>
              </tr>
            </thead>
            <tbody>
              {movies.map((movie, index) => {
                const movieData = findMovieByName(movie.name);
                const dropValue = movie.drop && movie.drop.trim() ? movie.drop.trim() : 'N/A';
                const isDropPercentage = dropValue !== 'N/A' && dropValue.includes('%');
                const dropDisplay = isDropPercentage ? `-${dropValue}` : dropValue;
                
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      {movieData ? (
                        <span 
                          className="table-movie-link"
                          onClick={() => navigate(`/movie/${movieData.slug || movieData.id}`)}
                          style={{ cursor: 'pointer', color: 'var(--accent)' }}
                        >
                          {movie.name}
                        </span>
                      ) : (
                        movie.name
                      )}
                    </td>
                    <td>{movie.weekCollections || 'N/A'}</td>
                    <td>{movie.totalCollections || 'N/A'}</td>
                    <td>
                      <span 
                        style={{ 
                          color: isDropPercentage ? '#ff4444' : 'inherit',
                          fontWeight: isDropPercentage ? '600' : 'normal'
                        }}
                      >
                        {dropDisplay}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    return <div>{elements}</div>;
  };

  // Re-check admin status when user prop changes
  useEffect(() => {
    checkAdminStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProp]);

  const fetchLatestReport = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API}/api/weekly-reports/latest`);
      setReport(res.data);
      if (res.data?.slug) {
        navigate(`/boxoffice/report/${res.data.slug}`, { replace: true });
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('');
        setReport(null);
      } else {
        setError('Failed to load weekly report');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReportByWeek = async (week) => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API}/api/weekly-reports?week=${week}`);
      if (res.data && res.data.length > 0) {
        const report = res.data[0];
        setReport(null); // We'll load fresh data once slug route resolves
        navigate(`/boxoffice/report/${report.slug}`, { replace: true });
      } else {
        setError('Weekly report not found for this week');
        setReport(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Weekly report not found for this week');
      } else {
        setError('Failed to load weekly report');
      }
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const checkAdminStatus = useCallback(async () => {
    if (!token) {
      setIsAdmin(false);
      return;
    }
    
    // Check admin status via dedicated endpoint (more secure)
    try {
      const res = await axios.get(`${API}/api/me/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAdmin(res.data.isAdmin || false);
    } catch (err) {
      setIsAdmin(false);
    }
  }, [token]);

  const fetchReportBySlug = async (reportSlug) => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API}/api/weekly-reports/${reportSlug}`);
      setReport(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Weekly report not found');
        setReport(null);
      } else {
        setError('Failed to load weekly report');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReportsList = async () => {
    try {
      const res = await axios.get(`${API}/api/weekly-reports`);
      setReportsList(res.data);
    } catch (err) {
      // Error fetching reports list
    }
  };

  const handleWeekSelect = (selectedReport) => {
    if (selectedReport.slug === report?.slug) {
      setShowDropdown(false);
      return;
    }
    navigate(`/boxoffice/report/${selectedReport.slug}`);
    setShowDropdown(false);
  };

  const formatWeek = (week) => {
    if (!week) return '';
    const [year, weekNum] = week.split('-');
    return `Week ${weekNum}, ${year}`;
  };

  const getCurrentWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const days = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
    const week = Math.ceil((days + startOfYear.getDay() + 1) / 7);
    return `${year}-${String(week).padStart(2, '0')}`;
  };

  const generateContentFromTemplate = () => {
    if (!useTemplate) return formData.content;

    let content = `## Movies Released This Week\n\n`;
    
    templateData.movies.forEach((movie, index) => {
      if (movie.name.trim()) {
        content += `### ${index + 1}. ${movie.name}\n\n`;
        
        if (movie.weekCollections.trim()) {
          content += `**This Week's Collections:** ${movie.weekCollections}\n\n`;
        }
        
        if (movie.totalCollections.trim()) {
          content += `**Total Collections (Till Now):** ${movie.totalCollections}\n\n`;
        }
        
        if (movie.drop && movie.drop.trim()) {
          content += `**Drop:** ${movie.drop}\n\n`;
        }
        
        const distributionEntries = Object.entries(movie.distribution).filter(([_, value]) => value.trim());
        if (distributionEntries.length > 0) {
          content += `**Distribution Breakdown:**\n`;
          distributionEntries.forEach(([region, amount]) => {
            const regionName = region.charAt(0).toUpperCase() + region.slice(1);
            content += `- ${regionName}: ${amount}\n`;
          });
          content += `\n`;
        }
        
        // Don't add separator line
      }
    });

    return content;
  };

  const handleAddMovie = () => {
    setTemplateData({
      ...templateData,
      movies: [...templateData.movies, {
        name: '',
        weekCollections: '',
        totalCollections: '',
        drop: '',
        distribution: {
          overseas: '',
          kannada: '',
          telugu: '',
          hindi: '',
          tamil: '',
          malayalam: '',
          other: ''
        }
      }]
    });
  };

  const handleRemoveMovie = (index) => {
    const newMovies = templateData.movies.filter((_, i) => i !== index);
    setTemplateData({
      ...templateData,
      movies: newMovies.length > 0 ? newMovies : [{
        name: '',
        weekCollections: '',
        totalCollections: '',
        drop: '',
        distribution: {
          overseas: '',
          kannada: '',
          telugu: '',
          hindi: '',
          tamil: '',
          malayalam: '',
          other: ''
        }
      }]
    });
  };

  const handleMovieChange = (index, field, value) => {
    const newMovies = [...templateData.movies];
    newMovies[index][field] = value;
    setTemplateData({ ...templateData, movies: newMovies });
  };

  const handleDistributionChange = (movieIndex, region, value) => {
    const newMovies = [...templateData.movies];
    newMovies[movieIndex].distribution[region] = value;
    setTemplateData({ ...templateData, movies: newMovies });
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const content = useTemplate ? generateContentFromTemplate() : formData.content;
      
      const res = await axios.post(
        `${API}/api/weekly-reports`,
        {
          title: formData.title,
          summary: formData.summary || null,
          content: content,
          movies: templateData.movies,
          week: formData.week || getCurrentWeek()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Weekly report created successfully!');
      setShowCreateForm(false);
      setFormData({ title: '', summary: '', content: '', week: '' });
      setTemplateData({
        movies: [{
          name: '',
          weekCollections: '',
          totalCollections: '',
          distribution: {
            overseas: '',
            kannada: '',
            telugu: '',
            hindi: '',
            tamil: '',
            malayalam: '',
            other: ''
          }
        }]
      });
      fetchReportsList();
      if (res.data?.slug) {
        navigate(`/boxoffice/report/${res.data.slug}`, { replace: true });
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create weekly report');
    } finally {
      setSubmitting(false);
    }
  };

  const parseContentToTemplate = (content) => {
    // Try to parse markdown content back to template format
    const movies = [];
    const lines = content.split('\n');
    let currentMovie = null;
    let inDistribution = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Check for movie heading (### 1. Movie Name)
      const movieMatch = line.match(/^###\s+\d+\.\s+(.+)$/);
      if (movieMatch) {
        if (currentMovie) {
          movies.push(currentMovie);
        }
        currentMovie = {
          name: movieMatch[1],
          weekCollections: '',
          totalCollections: '',
          drop: '',
          distribution: {
            overseas: '',
            kannada: '',
            telugu: '',
            hindi: '',
            tamil: '',
            malayalam: '',
            other: ''
          }
        };
        inDistribution = false;
        continue;
      }

      if (currentMovie) {
        // Check for week collections
        const weekMatch = line.match(/\*\*This Week's Collections:\*\*\s+(.+)/);
        if (weekMatch) {
          currentMovie.weekCollections = weekMatch[1];
          continue;
        }

        // Check for total collections
        const totalMatch = line.match(/\*\*Total Collections \(Till Now\):\*\*\s+(.+)/);
        if (totalMatch) {
          currentMovie.totalCollections = totalMatch[1];
          continue;
        }

        // Check for drop
        const dropMatch = line.match(/\*\*Drop:\*\*\s+(.+)/);
        if (dropMatch) {
          currentMovie.drop = dropMatch[1];
          continue;
        }

        // Check for distribution section
        if (line.includes('Distribution Breakdown')) {
          inDistribution = true;
          continue;
        }

        // Parse distribution items
        if (inDistribution && line.startsWith('-')) {
          const distMatch = line.match(/-\s+(\w+):\s+(.+)/);
          if (distMatch) {
            const region = distMatch[1].toLowerCase();
            const amount = distMatch[2].trim();
            if (currentMovie.distribution.hasOwnProperty(region)) {
              currentMovie.distribution[region] = amount;
            }
          }
        }
        
        // Stop parsing distribution if we hit a separator or new movie
        if (line === '---' && inDistribution) {
          inDistribution = false;
        }

        // End of distribution section
        if (line === '---' && inDistribution) {
          inDistribution = false;
        }
      }
    }

    if (currentMovie) {
      movies.push(currentMovie);
    }

    return movies.length > 0 ? movies : null;
  };

  const handleEditReport = (reportData) => {
    // Fetch movies only when editing (needed for template)
    if (allMovies.length === 0) {
      fetchAllMovies();
    }
    setEditingReport(reportData);
    setFormData({
      title: reportData.title,
      summary: reportData.summary || '',
      content: reportData.content,
      week: reportData.week
    });
    
    // Try to parse content to template format
    const parsedMovies = parseContentToTemplate(reportData.content);
    if (parsedMovies && parsedMovies.length > 0) {
      setTemplateData({ movies: parsedMovies });
      setUseTemplate(true);
    } else {
      setTemplateData({
        movies: [{
          name: '',
          weekCollections: '',
          totalCollections: '',
          drop: '',
          distribution: {
            overseas: '',
            kannada: '',
            telugu: '',
            hindi: '',
            tamil: '',
            malayalam: '',
            other: ''
          }
        }]
      });
      setUseTemplate(false);
    }
    
    setShowCreateForm(false);
  };

  const handleUpdateReport = async (e) => {
    e.preventDefault();
    if (!editingReport) return;

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const content = useTemplate ? generateContentFromTemplate() : formData.content;
      
      const res = await axios.put(
        `${API}/api/weekly-reports/${editingReport.slug}`,
        {
          title: formData.title,
          summary: formData.summary || null,
          content: content,
          movies: templateData.movies,
          week: formData.week
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Weekly report updated successfully!');
      setEditingReport(null);
      setFormData({ title: '', summary: '', content: '', week: '' });
      setTemplateData({
        movies: [{
          name: '',
          weekCollections: '',
          totalCollections: '',
          drop: '',
          distribution: {
            overseas: '',
            kannada: '',
            telugu: '',
            hindi: '',
            tamil: '',
            malayalam: '',
            other: ''
          }
        }]
      });
      fetchReportsList();
      if (res.data?.slug) {
        navigate(`/boxoffice/report/${res.data.slug}`, { replace: true });
      }
      setReport(res.data);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update weekly report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReport = async (reportSlug) => {
    if (!window.confirm('Are you sure you want to delete this weekly report?')) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      await axios.delete(
        `${API}/api/weekly-reports/${reportSlug}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Weekly report deleted successfully!');
      fetchReportsList();
      // If we deleted the current report, navigate to latest or main page
      if (reportSlug === slug || reportSlug === report?.slug) {
        try {
          const latestRes = await axios.get(`${API}/api/weekly-reports/latest`);
          if (latestRes.data?.slug) {
            navigate(`/boxoffice/report/${latestRes.data.slug}`, { replace: true });
          } else {
            navigate('/boxoffice', { replace: true });
          }
        } catch {
          navigate('/boxoffice', { replace: true });
        }
      }
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete weekly report');
    }
  };

  const cancelEdit = () => {
    setEditingReport(null);
    setFormData({ title: '', summary: '', content: '', week: '' });
      setTemplateData({
        movies: [{
          name: '',
          weekCollections: '',
          totalCollections: '',
          drop: '',
          distribution: {
            overseas: '',
            kannada: '',
            telugu: '',
            hindi: '',
            tamil: '',
            malayalam: '',
            other: ''
          }
        }]
      });
    setShowCreateForm(false);
  };

  if (loading) {
    return (
      <div className="weekly-report-detail-page">
        <div className="loading-indicator">Loading weekly report...</div>
      </div>
    );
  }

  return (
    <div className="weekly-report-detail-page media-report-layout">
            <Helmet>
          <title>{report?.title}</title>

          <meta
            name="description"
            content={
              report?.summary ||
              "Tollywood weekly box office collections, day-wise data, regional performance & more."
            }
          />
          <link rel="canonical" href={`https://tollyhype.com/boxoffice/report/${report?.slug}`}/>
          <meta property="og:title" content={report.title} />
          <meta property="og:description" content={report.summary} />
          <meta property="og:url" content={`https://tollyhype.com/boxoffice/report/${report.slug}`} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content="/logo512.png" />
          <script type="application/ld+json">
            {`
            {
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "${report.title}",
              "description": "${report.summary}",
              "author": {
                "@type": "Organization",
                "name": "TollyHype"
              },
              "publisher": {
                "@type": "Organization",
                "name": "TollyHype"
              },
              "url": "https://tollyhype.com/boxoffice/report/${report.slug}",
              "datePublished": "${report.createdAt}",
              "dateModified": "${report.updatedAt}"
            }
            `}
            </script>
        </Helmet>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {isAdmin && !showCreateForm && !editingReport && (
        <div className="admin-controls">
          <button
            className="admin-btn create-btn"
            onClick={() => {
              // Don't fetch movies for creating new report - not needed
              setShowCreateForm(true);
              setEditingReport(null);
              setFormData({ title: '', summary: '', content: '', week: getCurrentWeek() });
              setTemplateData({
                movies: [{
                  name: '',
                  weekCollections: '',
                  totalCollections: '',
                  drop: '',
                  distribution: {
                    overseas: '',
                    kannada: '',
                    telugu: '',
                    hindi: '',
                    tamil: '',
                    malayalam: '',
                    other: ''
                  }
                }]
              });
              setUseTemplate(true);
            }}
          >
            <MdAdd /> Create New Report
          </button>
        </div>
      )}

      {(showCreateForm || editingReport) && isAdmin && (
        <div className="admin-form-card">
          <div className="admin-form-header">
            <h2>{editingReport ? 'Edit Weekly Report' : 'Create New Weekly Report'}</h2>
            <button className="close-btn" onClick={cancelEdit}>
              <MdClose />
            </button>
          </div>
          <form onSubmit={editingReport ? handleUpdateReport : handleCreateReport}>
            <div className="form-group">
              <label>Week (YYYY-WW) *</label>
              <input
                type="text"
                value={formData.week}
                onChange={(e) => setFormData({ ...formData, week: e.target.value })}
                placeholder="2024-01"
                required
                pattern="\d{4}-\d{2}"
              />
              <small>Format: YYYY-WW (e.g., 2024-01). Leave empty to use current week.</small>
            </div>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Weekly Report Title"
                required
              />
            </div>
            <div className="form-group">
              <label>Summary</label>
              <textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder="Brief summary of the weekly report"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={useTemplate}
                  onChange={(e) => setUseTemplate(e.target.checked)}
                />
                Use Template (Recommended)
              </label>
              <small>Use the template form below to easily create structured reports</small>
            </div>

            {useTemplate ? (
              <div className="template-form">
                <div className="template-section">
                  <h3>Movies Released This Week</h3>
                  {templateData.movies.map((movie, index) => (
                    <div key={index} className="movie-card-template">
                      <div className="movie-card-header">
                        <h4>Movie {index + 1}</h4>
                        {templateData.movies.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveMovie(index)}
                            className="remove-movie-btn"
                          >
                            <MdClose /> Remove
                          </button>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label>Movie Name *</label>
                        <input
                          type="text"
                          placeholder="Enter movie name"
                          value={movie.name}
                          onChange={(e) => handleMovieChange(index, 'name', e.target.value)}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>This Week's Collections</label>
                        <input
                          type="text"
                          placeholder="e.g., ₹50 Crores"
                          value={movie.weekCollections}
                          onChange={(e) => handleMovieChange(index, 'weekCollections', e.target.value)}
                        />
                      </div>

                      <div className="form-group">
                        <label>Total Collections (Till Now)</label>
                        <input
                          type="text"
                          placeholder="e.g., ₹200 Crores"
                          value={movie.totalCollections}
                          onChange={(e) => handleMovieChange(index, 'totalCollections', e.target.value)}
                        />
                      </div>

                      <div className="form-group">
                        <label>Drop (Optional)</label>
                        <input
                          type="text"
                          placeholder="e.g., 53%"
                          value={movie.drop}
                          onChange={(e) => handleMovieChange(index, 'drop', e.target.value)}
                        />
                        <small>Enter percentage drop from previous week (e.g., 53%)</small>
                      </div>

                      <div className="distribution-section">
                        <h5>Distribution Breakdown</h5>
                        <div className="distribution-grid">
                          <div className="form-group">
                            <label>Overseas</label>
                            <input
                              type="text"
                              value={movie.distribution.overseas}
                              onChange={(e) => handleDistributionChange(index, 'overseas', e.target.value)}
                              placeholder="e.g., ₹20 Crores"
                            />
                          </div>
                          <div className="form-group">
                            <label>Kannada</label>
                            <input
                              type="text"
                              value={movie.distribution.kannada}
                              onChange={(e) => handleDistributionChange(index, 'kannada', e.target.value)}
                              placeholder="e.g., ₹15 Crores"
                            />
                          </div>
                          <div className="form-group">
                            <label>Telugu</label>
                            <input
                              type="text"
                              value={movie.distribution.telugu}
                              onChange={(e) => handleDistributionChange(index, 'telugu', e.target.value)}
                              placeholder="e.g., ₹30 Crores"
                            />
                          </div>
                          <div className="form-group">
                            <label>Hindi</label>
                            <input
                              type="text"
                              value={movie.distribution.hindi}
                              onChange={(e) => handleDistributionChange(index, 'hindi', e.target.value)}
                              placeholder="e.g., ₹25 Crores"
                            />
                          </div>
                          <div className="form-group">
                            <label>Tamil</label>
                            <input
                              type="text"
                              value={movie.distribution.tamil}
                              onChange={(e) => handleDistributionChange(index, 'tamil', e.target.value)}
                              placeholder="e.g., ₹10 Crores"
                            />
                          </div>
                          <div className="form-group">
                            <label>Malayalam</label>
                            <input
                              type="text"
                              value={movie.distribution.malayalam}
                              onChange={(e) => handleDistributionChange(index, 'malayalam', e.target.value)}
                              placeholder="e.g., ₹5 Crores"
                            />
                          </div>
                          <div className="form-group">
                            <label>Other</label>
                            <input
                              type="text"
                              value={movie.distribution.other}
                              onChange={(e) => handleDistributionChange(index, 'other', e.target.value)}
                              placeholder="e.g., ₹5 Crores"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleAddMovie}
                    className="add-movie-btn"
                  >
                    <MdAdd /> Add Another Movie
                  </button>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <label>Content (Markdown) *</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="# Weekly Report&#10;&#10;Write your markdown content here..."
                  rows="15"
                  required={!useTemplate}
                />
                <small>Supports Markdown formatting (headers, lists, links, etc.)</small>
              </div>
            )}
            <div className="form-actions">
              <button type="submit" className="submit-btn" disabled={submitting}>
                <MdSave /> {submitting ? 'Saving...' : (editingReport ? 'Update Report' : 'Create Report')}
              </button>
              <button type="button" className="cancel-btn" onClick={cancelEdit} disabled={submitting}>
                <MdClose /> Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="media-report-header">
        <div className="report-navigation">
          <div className="week-selector">
            <button
              className="week-selector-btn"
              onClick={() => {
                if (!showDropdown && reportsList.length === 0) {
                  // Fetch reports list only when dropdown is opened for the first time
                  fetchReportsList();
                }
                setShowDropdown(!showDropdown);
              }}
            >
              <MdCalendarToday />
              <span>{report ? formatWeek(report.week) : 'Select Week'}</span>
              <MdArrowDropDown className={showDropdown ? 'open' : ''} />
            </button>
            {showDropdown && reportsList.length > 0 && (
              <div className="week-dropdown">
                {reportsList.map((r) => (
                        <div key={r.id} className="week-dropdown-item-wrapper">
                          <button
                            className={`week-dropdown-item ${(slug === r.slug || (!slug && report?.slug === r.slug)) ? 'active' : ''}`}
                            onClick={() => handleWeekSelect(r)}
                          >
                            <div className="week-dropdown-week">{formatWeek(r.week)}</div>
                            <div className="week-dropdown-title">{r.title}</div>
                          </button>
                          {isAdmin && (
                            <div className="week-dropdown-actions">
                              <button
                                className="dropdown-edit-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditReport(r);
                                  setShowDropdown(false);
                                }}
                                title="Edit"
                              >
                                <MdEdit />
                              </button>
                              <button
                                className="dropdown-delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteReport(r.slug);
                                  setShowDropdown(false);
                                }}
                                title="Delete"
                              >
                                <MdDelete />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            </div>
          </div>

      {!showCreateForm && !editingReport && report && (
        <article className="media-report-article">
              {isAdmin && (
                <div className="admin-actions">
                  <button
                    className="admin-action-btn edit-btn"
                    onClick={() => handleEditReport(report)}
                  >
                    <MdEdit /> Edit
                  </button>
                  <button
                    className="admin-action-btn delete-btn"
                    onClick={() => handleDeleteReport(report.slug)}
                  >
                    <MdDelete /> Delete
                  </button>
                </div>
              )}

              <header className="media-report-header-content">
                <div className="report-meta-top">
                  <span className="report-category">Weekly Box Office Report</span>
                  <span className="report-date">
                    {new Date(report.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
                <h1 className="media-report-title">{report.title}</h1>
                {report.summary && (
                  <p className="media-report-lead">{report.summary}</p>
                )}
                <div className="report-meta-bottom">
                  <span className="report-week">{formatWeek(report.week)}</span>
                </div>
              </header>

              <div className="media-report-body">
                {renderMovies(report.content)}
              </div>
            </article>
      )}

      {!showCreateForm && !editingReport && !report && (
        <div className="no-report">
          <h2>Weekly Report Not Found</h2>
          <p>The report you're looking for doesn't exist or has been removed.</p>
        </div>
      )}
    </div>
  );
}
